import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Button as MuiButton,
  Chip,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import Button from "../../../common/Button";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import CardUI from "../../../common/Card";
import EmptyBox from "../../../assets/images/empty-box.png";
import AddIcon from "@mui/icons-material/Add";
import {
  caseStatusType,
  shortDateFormat,
  timeDateFormat,
} from "../../../utils/helpers";
import { InfoCircle, SecurityIcon } from "../../../assets/constants";
import Dialog from "@mui/material/Dialog";
import PushNotificationForm from "../../../components/PushNotificationForm";
import { useDispatch, useSelector } from "react-redux";
import { getNotificationsListFunc } from "../../../actions/Referrals";
import { getAgencyDetailFunc } from "../../../actions/Agency";
import { segmentTrack } from "../../../utils/helpers/tracking_utils";
import AlertMessage from "../../../common/AlertMessage";
import CircularProgressLoading from "../../../common/CircularProgress";
import SkeletonGrid from "../../../components/SkeletonGrid";
import CaseTableSkeleton from "../../../components/CaseTableSkeleton";

import { Link, useNavigate } from 'react-router-dom';

const PushNotifications = () => {
  const navigate = useNavigate();
  const [notificationsList, setNotificationsList] = useState([]);
  const [openDialog, setopenDialog] = useState(false);
  const [permissionMessage, setPermissionMessage] = useState(null);

  const dispatch = useDispatch();
  const getNotificationsList = useSelector(
    (state) => state.getNotificationsList
  );
  const { loading, error, notifications } = getNotificationsList;

  const agentProfile = useSelector((state) => state.agentProfile);
  const { loading: loadingAgent, error: errorAgent, agentUser } = agentProfile;

  const getAgencyDetail = useSelector((state) => state.getAgencyDetail);
  const {
    loading: loadingAgency,
    error: errorAgency,
    agencyDetail,
  } = getAgencyDetail;

  useEffect(() => {
    if (agentUser?.agent && agencyDetail?.agency) {
      segmentTrack({
        event: "push_notifications",
        agent: agentUser?.agent,
        agency: agencyDetail?.agency,
      });
    }
  }, [agentUser, agencyDetail]);

  useEffect(() => {
    dispatch(getAgencyDetailFunc());
  }, []);

  const agentUserRef = useRef();

  useEffect(() => {
    agentUserRef.current = agentUser;
  }, [agentUser]);

  const [openAlert, setOpenAlert] = useState(true);
  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }

    setOpenAlert(false);
    setPermissionMessage(null);
  };

  useEffect(() => {
    // setNotificationsList(notificationsData);
    dispatch(getNotificationsListFunc());
  }, []);

  useEffect(() => {
    if (notifications?.notifications) {
      setNotificationsList(notifications?.notifications);
    }
  }, [notifications]);

  const [query, setQuery] = useState("");


  const rows = useMemo(() => {
    if (!notificationsList.length) return [];

    return notificationsList
      .filter(
        ({ title, body }) =>
          String(title || '').toLowerCase().includes(query.toLowerCase()) || String(body || '').toLowerCase().includes(query.toLowerCase())
      )
      .sort((a, b) =>
        (Date.parse(b.created_at || b.notification_time) || 0) -
        (Date.parse(a.created_at || a.notification_time) || 0) || Number(b.id) - Number(a.id)
      )
      .map((notification) => ({
        id: notification?.id,
        message: {
          messageTitle: notification?.title ? notification?.title : "",
          messageDescription: notification?.body ? notification?.body : "",
        },
        status: {
          type: notification?.status ? notification?.status : "",
          createdAt: notification?.notification_time
            ? shortDateFormat(notification?.notification_time)
            : "",
        },
        deliveryCount: notification?.total_devices_delivered || 0,
        openCount: notification?.total_devices_opened || 0,
        targeted: notification?.total_devices_targeted || 0,
        historical: notification?.tracking_version !== 2,
      }));
  }, [notificationsList, query]); // clientsList, query

  const columns = useMemo(
    () => [
      {
        field: "message",
        headerName: "Message",
        width: 360,
        renderCell: ({ value, row }) => (
          <Stack>
            <Typography component={Link} to={`/marketing/push-notifications/${row.id}`} onClick={event => event.stopPropagation()} variant="body1" sx={{ color: "text.primary", textDecoration: "none", fontWeight: 600 }}>{value.messageTitle}</Typography>
            <Typography variant="body2" color="rgba(0, 0, 0, 0.38)">
              {value.messageDescription}
            </Typography>
          </Stack>
        ),
        sortable: false,
      },
      {
        field: "status",
        headerName: "Status",
        renderCell: ({ value }) => (
          <Chip
            size="small"
            label={`${value.type} ${value.createdAt}`}
            sx={{ textTransform: "capitalize" }}
            variant="outlined"
          />
        ),
        width: 210,
        sortable: false,
      },
      {
        field: "targeted", headerName: "Audience", width: 125,
        renderCell: ({ value, row }) => <MuiButton color="inherit" component={Link} to={`/marketing/push-notifications/${row.id}`} onClick={event => event.stopPropagation()}>{value}</MuiButton>,
      },
      {
        field: "deliveryCount",
        renderHeader: () => (
          <Box display="flex" gap="4px" alignItems="flex-start">
            <Typography
              variant="h2"
              fontSize="14px"
              fontWeight="500"
              lineHeight="28px"
            >
              Viewed
            </Typography>
            <Tooltip
              title="Viewed means the app confirmed receipt on a device, not that the person saw or read it. Some receipts cannot be confirmed. Historical totals may include duplicate reports."
              placement="top"
              arrow
              PopperProps={{
                sx: {
                  marginBottom: "-7px !important",
                },
              }}
            >
              <InfoCircle />
            </Tooltip>
          </Box>
        ),

        renderCell: ({ value, row }) => (
          <Tooltip
            title={`${row.deliveryCount} of ${row.targeted} users${
              row.historical ? ". Historical estimate; preserved from previous tracking." : ""
            }`}
          >
            <MuiButton color="inherit" component={Link} to={`/marketing/push-notifications/${row.id}?tab=${row.historical ? "all" : "viewed"}`} onClick={event => event.stopPropagation()}>
              {value}
            </MuiButton>
          </Tooltip>
        ),
        sortable: false,
        minWidth: 190,
        flex: 1,
      },
      {
        field: "openCount",
        renderHeader: () => (
          <Box display="flex" gap="4px" alignItems="flex-start">
            <Typography
              variant="h2"
              fontSize="14px"
              fontWeight="500"
              lineHeight="28px"
            >
              Interacted
            </Typography>
            <Tooltip
              title="Number of selected users who tapped the push notification. Confirmed opens require the updated mobile app; viewing the notification list does not count."
              placement="top"
              arrow
              PopperProps={{
                sx: {
                  marginBottom: "-7px !important",
                },
              }}
            >
              <InfoCircle />
            </Tooltip>
          </Box>
        ),
        renderCell: ({ value, row }) => (
          <Tooltip
            title={`${row.openCount} of ${row.targeted} users${
              row.historical ? ". Historical estimate; preserved from previous tracking." : ""
            }`}
          >
            <MuiButton color="inherit" component={Link} to={`/marketing/push-notifications/${row.id}?tab=${row.historical ? "all" : "interacted"}`} onClick={event => event.stopPropagation()}>
              {value}
            </MuiButton>
          </Tooltip>
        ),
        sortable: false,
        minWidth: 190,
        flex: 1,
      },
      // {
      //   field: "actions",
      //   type: "actions",
      //   flex: 1,
      //   width: 80,
      //   getActions: (params) => [
      //     <GridActionsCellItem label="Edit" showInMenu />,
      //     <GridActionsCellItem
      //       label="Delete"
      //       onClick={() => console.log(params.row)}
      //       showInMenu
      //       sx={{ color: "red" }}
      //     />,
      //   ],
      // },
    ],
    []
  );

  const openNotificationFormHandler = () => {
    const access =
      agentUserRef.current?.agent?.permissions?.access_push_notifications
        ?.create;
    if (!access) {
      setPermissionMessage("You do not have permission to send a new message!");
      setOpenAlert(true);
      return;
    } else {
      setopenDialog((prevState) => !prevState);

      if (agentUser?.agent && agencyDetail?.agency) {
        segmentTrack({
          event: "new_pn_clicked",
          agent: agentUser?.agent,
          agency: agencyDetail?.agency,
        });
      }
    }
  };

  return (
    <Stack height="742px">
      {openDialog && (
        <Dialog open={openDialog} fullWidth maxWidth="lg" PaperProps={{ sx: { maxWidth: 1040, maxHeight: "calc(100% - 48px)", m: 3 } }}>
          <PushNotificationForm handleClose={openNotificationFormHandler} onCreated={notification => { setopenDialog(false); navigate(`/marketing/push-notifications/${notification.id}`); }} />
        </Dialog>
      )}
      {!loading && error && (
        <AlertMessage
          message={error}
          severity="error"
          open={openAlert}
          handleClose={handleClose}
        />
      )}
      {!loading && permissionMessage && (
        <AlertMessage
          message={permissionMessage}
          severity="error"
          open={openAlert}
          handleClose={handleClose}
        />
      )}

      <CardUI maxWidth="100%" dashboard="true" responsive="true" height="100%" padding="32px">
        {loading ? (
          <CaseTableSkeleton
            headers={["Message", "Status", "Audience", "Viewed", "Interacted"]}
            numRows={4}
          />
        ) : notificationsList?.length > 0 ? (
          <Stack gap="24px" height="100%">
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box display="flex" gap="16px">
                <FormControl size="small" variant="outlined">
                  <InputLabel htmlFor="search-messages">Search</InputLabel>
                  <OutlinedInput
                    id="search-messages"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    endAdornment={
                      <InputAdornment position="end">
                        <SearchOutlinedIcon />
                      </InputAdornment>
                    }
                    label="Search"
                    size="small"
                    sx={{
                      backgroundColor: "#FFF",
                      minWidth: "414px",
                      width: "100%",
                    }}
                  />
                </FormControl>
              </Box>

              {notificationsList.length > 0 && (
                <Button
                  startIcon={<AddIcon />}
                  title="New Message"
                  color="primary"
                  fontSize="15px"
                  variant="contained"
                  onClick={openNotificationFormHandler}
                />
              )}
            </Box>

            <div
              style={{
                height: "613px",
                width: "100%",
                lineHeight: "normal !important",
                // maxWidth: "1200px",
                // overflowX: "scroll",
              }}
            >
              <DataGrid
                rows={rows}
                columns={columns}
                getRowHeight={() => "auto"}
                pageSize={10}
                rowsPerPageOptions={[10]}
                // initialState={{
                //   pagination: {
                //     paginationModel: { page: 0, pageSize: 5 },
                //   },
                // }}
                // rowsPerPageOptions={[5, 10]}
                sx={{
                  cursor: "pointer !important",
                  "&": { border: "none" },
                  [`& .${gridClasses.cell}`]: {
                    py: "16px",
                  },
                }}
                onRowClick={({ id }) => navigate(`/marketing/push-notifications/${id}`)}
                disableSelectionOnClick
              />
            </div>
          </Stack>
        ) : (
          <Stack
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={2}
          >
            <img src={EmptyBox} alt="add notifications" />
            <Typography
              variant="body2"
              textAlign="center"
              color="rgba(0, 0, 0, 0.38)"
            >
              Start Sending push notifications to your clients.
            </Typography>
            <Button
              startIcon={<AddIcon />}
              title="New Message"
              color="primary"
              fontSize="15px"
              variant="contained"
              onClick={openNotificationFormHandler}
            />
          </Stack>
        )}
      </CardUI>
    </Stack>
  );
};

export default PushNotifications;
